import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { messaggioDi } from "@/lib/messaggio-errore";
import { useConferma } from "@/hooks/use-conferma";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

/**
 * I numeri da cui esce ogni preventivo: i cinque livelli di ricarico, gli sconti
 * d'acquisto per categoria, il livello più basso a cui ogni ruolo può scendere.
 *
 * Il calcolatore li legge dal database a ogni calcolo: una modifica salvata qui
 * vale dal calcolo successivo. Ogni modifica resta nel Registro, col prima e il
 * dopo, e la scrive il database (non questa pagina). L'ordine M1 < … < M5 lo
 * impone il database; qui lo si controlla prima, per dire quale livello non torna.
 */

const LIVELLI = ["M1", "M2", "M3", "M4", "M5"];
const CATEGORIE = {
  mobilio: "Mobilio",
  top: "Top",
  elettrodomestici: "Elettrodomestici",
  promozione: "Promozione",
};
const RUOLI = { titolare: "Titolare", venditore: "Venditore" };

const cifra = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 3, useGrouping: false });
const testoNumero = (n) => cifra.format(Number(n));
const leggiNumero = (t) => {
  const pulito = String(t ?? "").trim().replace(",", ".");
  if (!/^\d+(\.\d{1,3})?$/.test(pulito)) return null;
  return Number(pulito);
};
const testoCatena = (sconti) => (sconti || []).map(testoNumero).join(" + ");
const leggiCatena = (t) => {
  const pezzi = String(t ?? "").split("+").map((x) => x.trim());
  if (!pezzi.length || pezzi.length > 4 || pezzi.some((x) => x === "")) return null;
  const numeri = pezzi.map(leggiNumero);
  return numeri.some((n) => n === null || n >= 100) ? null : numeri;
};

function Blocco({ titolo, nota, children, testId }) {
  return (
    <Card className="p-card" data-testid={testId}>
      <h2 className="text-base font-semibold text-foreground">{titolo}</h2>
      {nota ? <p className="mt-micro max-w-2xl text-sm text-muted-foreground">{nota}</p> : null}
      <div className="mt-group">{children}</div>
    </Card>
  );
}

export default function PrezziTab() {
  const { chiedi, dialogoConferma } = useConferma();
  const [dati, setDati] = useState(null);
  const [livelli, setLivelli] = useState({});
  const [catene, setCatene] = useState({});
  const [errore, setErrore] = useState(null);
  const [salva, setSalva] = useState(null); // quale blocco sta salvando

  /* Dopo un salvataggio si riallinea SOLO il blocco salvato: le modifiche non
     ancora salvate dell'altro blocco restano nei campi. I valori «di prima» con
     cui si confronta vengono sempre da `dati`, cioè dal server. */
  const riempi = (d, blocchi = ["livelli", "catene"]) => {
    setDati(d);
    if (blocchi.includes("livelli"))
      setLivelli(Object.fromEntries(d.ricarichi.map((r) => [r.livello, testoNumero(r.percentuale)])));
    if (blocchi.includes("catene"))
      setCatene(Object.fromEntries(d.catene.map((c) => [c.categoria, testoCatena(c.sconti)])));
  };

  useEffect(() => {
    api
      .get("/impostazioni/prezzi")
      .then((r) => riempi(r.data))
      .catch((e) => setErrore(messaggioDi(e)));
  }, []);

  if (errore && !dati) return <p className="text-sm text-danger-foreground">{errore}</p>;
  if (!dati) return <Skeleton className="h-72 w-full rounded-xl" />;

  /* ── Livelli ── */
  const prima = Object.fromEntries(dati.ricarichi.map((r) => [r.livello, Number(r.percentuale)]));
  const nuovi = Object.fromEntries(LIVELLI.map((l) => [l, leggiNumero(livelli[l])]));
  const nonNumeri = LIVELLI.filter((l) => nuovi[l] === null || nuovi[l] <= 0 || nuovi[l] >= 500);
  const fuoriOrdine = LIVELLI.slice(1).find((l, i) => nuovi[l] !== null && nuovi[LIVELLI[i]] !== null && nuovi[l] <= nuovi[LIVELLI[i]]);
  const cambiati = LIVELLI.filter((l) => nuovi[l] !== null && nuovi[l] !== prima[l]);
  const problemaLivelli = nonNumeri.length
    ? `${nonNumeri.join(", ")}: scrivi un numero fra 0 e 500, al massimo tre decimali.`
    : fuoriOrdine
      ? `${fuoriOrdine} deve stare sopra ${LIVELLI[LIVELLI.indexOf(fuoriOrdine) - 1]}.`
      : null;

  const salvaLivelli = async () => {
    const ok = await chiedi({
      titolo: "Cambiare i livelli di prezzo?",
      descrizione: `Dal prossimo calcolo: ${cambiati.map((l) => `${l} da ${testoNumero(prima[l])} % a ${testoNumero(nuovi[l])} %`).join(", ")}. I preventivi già salvati non cambiano finché non li ricalcoli. La modifica resta nel Registro.`,
      verbo: "Salva i livelli",
    });
    if (!ok) return;
    setSalva("livelli");
    try {
      const r = await api.put("/impostazioni/prezzi/ricarichi", {
        livelli: Object.fromEntries(cambiati.map((l) => [l, String(nuovi[l])])),
      });
      riempi(r.data, ["livelli"]);
      toast.success("Livelli salvati. Il prossimo calcolo usa i numeri nuovi.");
    } catch (e) {
      toast.error(messaggioDi(e));
    } finally {
      setSalva(null);
    }
  };

  /* ── Catene ── */
  const primaCatene = Object.fromEntries(dati.catene.map((c) => [c.categoria, testoCatena(c.sconti)]));
  const catenaCambiata = Object.keys(CATEGORIE).filter((k) => catene[k] !== undefined && catene[k] !== primaCatene[k]);
  const catenaSbagliata = catenaCambiata.filter((k) => leggiCatena(catene[k]) === null);

  const salvaCatene = async () => {
    const ok = await chiedi({
      titolo: "Cambiare gli sconti d'acquisto?",
      descrizione: `Dal prossimo calcolo: ${catenaCambiata.map((k) => `${CATEGORIE[k]} da ${primaCatene[k]} a ${testoCatena(leggiCatena(catene[k]))}`).join(", ")}. Cambia il costo, quindi tutti i prezzi di quella categoria.`,
      verbo: "Salva gli sconti",
    });
    if (!ok) return;
    setSalva("catene");
    const salvate = [];
    try {
      for (const k of catenaCambiata) {
        const r = await api.put(`/impostazioni/prezzi/catene/${k}`, { sconti: leggiCatena(catene[k]).map(String) });
        salvate.push(k);
        setDati(r.data);
      }
      riempi((await api.get("/impostazioni/prezzi")).data, ["catene"]);
      toast.success("Sconti salvati.");
    } catch (e) {
      // Una categoria può essere passata e la successiva no: si rilegge il server,
      // così quella salvata non resta segnata «da salvare», e il resto dei campi no.
      toast.error(
        salvate.length
          ? `Salvate: ${salvate.map((k) => CATEGORIE[k]).join(", ")}. Le altre no: ${messaggioDi(e)}`
          : messaggioDi(e),
      );
      try {
        setDati((await api.get("/impostazioni/prezzi")).data);
      } catch (_) {}
    } finally {
      setSalva(null);
    }
  };

  /* ── Soglie ── */
  const cambiaSoglia = async (ruolo, livello) => {
    const attuale = dati.soglie.find((s) => s.ruolo === ruolo)?.livello_minimo;
    if (livello === attuale) return;
    const ok = await chiedi({
      titolo: `Cambiare la soglia del ${RUOLI[ruolo].toLowerCase()}?`,
      descrizione: `Da ${attuale} a ${livello}: il ${RUOLI[ruolo].toLowerCase()} non potrà chiudere un preventivo sotto ${livello}. Il codice del prezzo minimo nel calcolatore cambia di conseguenza.`,
      verbo: "Cambia la soglia",
    });
    if (!ok) return;
    setSalva("soglie");
    try {
      const r = await api.put(`/impostazioni/prezzi/soglie/${ruolo}`, { livello_minimo: livello });
      riempi(r.data, []);
      toast.success("Soglia salvata.");
    } catch (e) {
      toast.error(messaggioDi(e));
    } finally {
      setSalva(null);
    }
  };

  return (
    <div className="space-y-card" data-testid="prezzi-tab">
      {dialogoConferma}

      <Blocco
        titolo="Livelli di ricarico"
        nota="Il ricarico sul costo, da M1 (il più basso) a M5. Ogni livello deve stare sopra quello prima. Valgono dal prossimo calcolo."
        testId="prezzi-livelli"
      >
        <div className="grid gap-group sm:grid-cols-5">
          {LIVELLI.map((l) => {
            const cambiato = nuovi[l] !== null && nuovi[l] !== prima[l];
            return (
              <div key={l} className="flex items-center justify-between gap-group sm:block">
                <Label htmlFor={`livello-${l}`} className="text-sm font-medium text-foreground">
                  {l}
                </Label>
                <div className="relative w-32 sm:mt-inside sm:w-full">
                  <Input
                    id={`livello-${l}`}
                    inputMode="decimal"
                    value={livelli[l] ?? ""}
                    onChange={(e) => setLivelli((x) => ({ ...x, [l]: e.target.value }))}
                    className={cn("h-11 pr-8 text-right tabular-nums sm:h-9", cambiato ? "border-primary" : "")}
                    data-testid={`prezzi-livello-${l}`}
                    aria-describedby="livelli-stato"
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground" aria-hidden>
                    %
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-group flex flex-col gap-inside sm:flex-row sm:items-center sm:justify-between">
          <p id="livelli-stato" className={cn("text-sm", problemaLivelli ? "text-danger-foreground" : "text-muted-foreground")}>
            {problemaLivelli ||
              (cambiati.length
                ? `Da salvare: ${cambiati.join(", ")}.`
                : "Nessuna modifica.")}
          </p>
          <Button
            onClick={salvaLivelli}
            disabled={!cambiati.length || Boolean(problemaLivelli) || salva !== null}
            className="h-11 sm:h-9"
            data-testid="prezzi-salva-livelli"
          >
            {salva === "livelli" ? "Salvo…" : "Salva i livelli"}
          </Button>
        </div>
      </Blocco>

      <Blocco
        titolo="Sconti d'acquisto per categoria"
        nota="Gli sconti che il fornitore fa sul listino, applicati uno dopo l'altro. Si scrivono separati da «+», per esempio 10 + 5."
        testId="prezzi-catene"
      >
        <div className="space-y-group">
          {Object.entries(CATEGORIE).map(([k, nome]) => {
            const sbagliata = catenaSbagliata.includes(k);
            return (
              <div key={k} className="flex items-center justify-between gap-group">
                <Label htmlFor={`catena-${k}`} className="text-sm font-medium text-foreground">
                  {nome}
                </Label>
                <div className="relative w-40">
                  <Input
                    id={`catena-${k}`}
                    value={catene[k] ?? ""}
                    onChange={(e) => setCatene((x) => ({ ...x, [k]: e.target.value }))}
                    className={cn(
                      "h-11 pr-8 text-right tabular-nums sm:h-9",
                      sbagliata ? "border-danger" : catenaCambiata.includes(k) ? "border-primary" : "",
                    )}
                    aria-invalid={sbagliata}
                    data-testid={`prezzi-catena-${k}`}
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground" aria-hidden>
                    %
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-group flex flex-col gap-inside sm:flex-row sm:items-center sm:justify-between">
          <p className={cn("text-sm", catenaSbagliata.length ? "text-danger-foreground" : "text-muted-foreground")}>
            {catenaSbagliata.length
              ? `${catenaSbagliata.map((k) => CATEGORIE[k]).join(", ")}: da uno a quattro sconti fra 0 e 100, separati da «+».`
              : catenaCambiata.length
                ? `Da salvare: ${catenaCambiata.map((k) => CATEGORIE[k]).join(", ")}.`
                : "Nessuna modifica."}
          </p>
          <Button
            onClick={salvaCatene}
            disabled={!catenaCambiata.length || catenaSbagliata.length > 0 || salva !== null}
            className="h-11 sm:h-9"
            data-testid="prezzi-salva-catene"
          >
            {salva === "catene" ? "Salvo…" : "Salva gli sconti"}
          </Button>
        </div>
      </Blocco>

      <Blocco
        titolo="Livello minimo per ruolo"
        nota="Il livello più basso a cui ognuno può chiudere un preventivo. Sotto, il calcolatore non lo lascia scendere."
        testId="prezzi-soglie"
      >
        <div className="space-y-group">
          {dati.soglie.map((s) => (
            <div key={s.ruolo} className="flex items-center justify-between gap-group">
              <Label htmlFor={`soglia-${s.ruolo}`} className="text-sm font-medium text-foreground">
                {RUOLI[s.ruolo] || s.ruolo}
              </Label>
              <Select value={s.livello_minimo} onValueChange={(v) => cambiaSoglia(s.ruolo, v)} disabled={salva !== null}>
                <SelectTrigger id={`soglia-${s.ruolo}`} className="h-11 w-32 sm:h-9" data-testid={`prezzi-soglia-${s.ruolo}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LIVELLI.map((l) => (
                    <SelectItem key={l} value={l}>
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </Blocco>
    </div>
  );
}
